import React, { useContext } from 'react';
import { IntlShape } from 'react-intl';
import { cloneDeep } from 'lodash';
import { assign } from 'xstate';
import { Box, Button, Typography } from '@mui/material';

import { SendContext } from '../../SendContext';
import { BaseComponent } from '../Component';
import { MachineContext } from '../../MachineContext';
import { translateWithFallback } from '../../util';
import theme from '../../theme/theme';

export interface ScaleButtonComponent extends BaseComponent {
  type: 'scaleButton';
  props: ScaleButtonProps;
}
export interface ScaleButtonProps {
  saveResultTo?: string;
  scaleMin?: number;
  scaleMax?: number;
  minText?: string;
  maxText?: string;
}

export const ScaleButton: React.FC<ScaleButtonProps> = ({
  scaleMin = 1,
  scaleMax = 10,
  minText = '1 - unwahrscheinlich',
  maxText = '10 - sehr wahrscheinlich',
  saveResultTo = 'nps',
}) => {
  const { send } = useContext(SendContext);

  const styleArray = [
    {
      background: `${theme.palette.error.main}`,
    },
    {
      background: `linear-gradient(90deg, ${theme.palette.error.main} 55%, ${theme.palette.primary.light} 100%)`,
    },
    {
      background: `linear-gradient(90deg, ${theme.palette.error.main} 30%, ${theme.palette.primary.light} 100%)`,
    },
    {
      background: `linear-gradient(90deg, ${theme.palette.error.main} 0%, ${theme.palette.primary.light} 100%)`,
    },
    {
      background: `linear-gradient(90deg, ${theme.palette.error.main} -50%, ${theme.palette.primary.light} 100%)`,
    },
    {
      background: `linear-gradient(270deg, ${theme.palette.success.light} -50%, ${theme.palette.primary.light} 100%)`,
    },
    {
      background: `linear-gradient(270deg, ${theme.palette.success.light} 0%, ${theme.palette.primary.light} 100%)`,
    },
    {
      background: `linear-gradient(270deg, ${theme.palette.success.light} 30%, ${theme.palette.primary.light} 100%)`,
    },
    {
      background: `linear-gradient(270deg, ${theme.palette.success.light} 55%, ${theme.palette.primary.light} 100%)`,
    },
    {
      background: `${theme.palette.success.light}`,
    },
  ];

  function getBackgroundStyle(scaleValue: number) {
    let result;
    if (scaleValue <= 0) {
      result = styleArray[0].background;
    } else if (scaleValue >= styleArray.length) {
      result = styleArray[styleArray.length - 1].background;
    } else {
      result = styleArray[scaleValue - 1].background;
    }
    return result;
  }

  const range = (start: number, stop: number, step: number): Array<number> =>
    Array.from(
      { length: (stop - start) / step + 1 },
      (_, i) => start + i * step
    );

  // somehow payload properties are stringified, thus need to be converted to numbers
  const rangeStart = Number(scaleMin);
  const rangeEnd = Number(scaleMax);

  let scaleValues = range(rangeStart, rangeEnd, 1);

  const buttonArray = scaleValues.map((scaleValue, i) => {
    const absoluteValueCount = scaleValues.length;
    const widthPercentage = 100 / (absoluteValueCount + 1);
    return (
      <Button
        key={'scalebutton' + scaleValue}
        sx={{
          width: `${widthPercentage}%`,
          minWidth: '4%',
          maxWidth: '15%',
          aspectRatio: '1 / 1',
          background: getBackgroundStyle(
            Math.round((i / absoluteValueCount) * 10)
          ),
          borderRadius: '50%',
          padding: 0,
          typography: scaleValues.length > 5 ? 'body2' : 'body1',
        }}
        onClick={() => {
          send({
            type: 'writeToContext',
            payload: { [saveResultTo]: scaleValue },
          });
          send({ type: 'next' });
        }}
      >
        {scaleValue}
      </Button>
    );
  });

  return (
    <Box display="flex" flexDirection="column">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '15px',
        }}
      >
        <Typography>{minText}</Typography>
        <Typography>{maxText}</Typography>
      </Box>
      <Box
        sx={{
          paddingBottom: '15px',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        {buttonArray}
      </Box>
    </Box>
  );
};

export const renderScaleButtonAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );
    const payload = actionMetadata.action.payload;
    newContext.widgetData.push({
      type: 'scaleButton',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        saveResultTo: payload.saveResultTo,
        minText: translateWithFallback(intl, payload.minText),
        maxText: translateWithFallback(intl, payload.maxText),
        scaleMin: payload.scaleMin,
        scaleMax: payload.scaleMax,
      },
    });
    return newContext;
  });
};
